/* role="list" on the <ol> is intentional, not redundant: `list-style: none` drops
   list semantics in Safari/VoiceOver, so the explicit role is required to keep
   "list, item n of N" context for screen-reader users. */
/* eslint-disable jsx-a11y/no-redundant-roles */
import { useNavigate } from 'react-router-dom';
import { Card } from './Card';
import { Button } from './Button';
import { InlineAlert } from './InlineAlert';
import { HelpTip } from './HelpTip';
import type { GettingStartedStep, StepState } from './gettingStartedSteps';
import styles from './GettingStarted.module.css';

export interface GettingStartedProps {
  steps: GettingStartedStep[];
  complete: boolean;
  currentStepNumber: number | null;
  /** Omit to hide the "Hide" control (e.g. the first-run page, where it's the only content). */
  onDismiss?: () => void;
}

const STATE_LABEL: Record<StepState, string> = {
  done: 'Done',
  current: 'Current step',
  upcoming: 'Not started',
};

/**
 * Persistent setup-progress checklist on Home (Slice 1). Shows the merchant where
 * they are and the single next action, in plain language. Pure presentation — the
 * parent derives `steps`/`complete` and owns dismissal persistence.
 */
export function GettingStarted({ steps, complete, currentStepNumber, onDismiss }: GettingStartedProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <div className={styles.header}>
        <h2 className={styles.heading}>Getting started</h2>
        <div className={styles.headerMeta}>
          {!complete && currentStepNumber !== null ? (
            <span className={styles.progress}>
              Step {currentStepNumber} of {steps.length}
            </span>
          ) : null}
          {onDismiss ? (
            <Button variant="ghost" onClick={onDismiss} aria-label="Hide getting started">
              Hide
            </Button>
          ) : null}
        </div>
      </div>

      {complete ? (
        <InlineAlert severity="success">
          You&apos;re all set — we&apos;re watching your store. We&apos;ll flag anything that looks
          off for you to review.
        </InlineAlert>
      ) : (
        <ol className={styles.list} role="list">
          {steps.map((step, index) => (
            <li
              key={step.key}
              className={`${styles.step} ${styles[step.state]}`}
              aria-current={step.state === 'current' ? 'step' : undefined}
            >
              <span className={styles.indicator} aria-hidden="true">
                {step.state === 'done' ? '✓' : index + 1}
              </span>
              <span className={styles.srOnly}>{STATE_LABEL[step.state]}: </span>
              <div className={styles.content}>
                <span className={styles.title}>
                  {step.title}
                  {step.helpTerm ? <HelpTip term={step.helpTerm} /> : null}
                </span>
                <span className={styles.description}>{step.description}</span>
              </div>
              {step.cta ? (
                <div className={styles.cta}>
                  <Button
                    variant="primary"
                    onClick={() => navigate(step.cta!.to)}
                    aria-label={`${step.cta.label} — step ${index + 1}`}
                  >
                    {step.cta.label}
                  </Button>
                </div>
              ) : step.link ? (
                <div className={styles.cta}>
                  <Button
                    variant="ghost"
                    onClick={() => navigate(step.link!.to)}
                    aria-label={`${step.link.label} — step ${index + 1}`}
                  >
                    {step.link.label}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
